import {
  Braces,
  ClipboardCheck,
  Database,
  Gauge,
  GitPullRequest,
  PanelTop,
  Route,
  ServerCog,
  ShieldCheck,
  Workflow,
} from "lucide-react";

const scientificAiSolutions = [
  {
    id: "private-evaluation-dataset",
    category: "data",
    group: "Evaluate",
    mode: "Dataset build",
    availability: "Scoped pilot",
    icon: Database,
    title: "Build a private evaluation set from your own science",
    summary:
      "Turn reviewed methods, edge cases, and failure records into held-out cases for one defined scientific task.",
    bestFor:
      "Scientific AI teams that have a working use case and domain experts who can judge the answer.",
    deliverables: [
      "Task specification, inclusion rules, and failure taxonomy",
      "Reviewed cases with provenance, reference decisions, and evaluation-only splits",
      "Versioned release, limitations, and a process for adding new failures",
    ],
    boundary:
      "Evaluation cases stay separate from model-training data. The set measures the task in scope; it does not certify general scientific ability.",
    cta: "Define an evaluation set",
  },
  {
    id: "agent-evaluation-harness",
    category: "data",
    group: "Evaluate",
    mode: "Evaluation sprint",
    availability: "Scoped pilot",
    icon: Gauge,
    title: "Know where your scientific agent fails",
    summary:
      "Run repeatable evals on real scientific work, save every output, and turn failure cases into release gates.",
    bestFor:
      "Teams deciding what an agent can draft, which changes need review, and what should block a release.",
    deliverables: [
      "Versioned task suite and workflow-specific failure taxonomy",
      "Evaluation runner capturing model, prompt, tools, configuration, outputs, and reviewer decisions",
      "Baseline report and regression gates for accept, revise, escalate, or block",
    ],
    boundary:
      "The result applies to the tested workflow, model, prompts, tools, and data. It is not a blanket claim of model reliability.",
    cta: "Build an eval suite",
    featured: true,
  },
  {
    id: "model-training-datasets",
    category: "data",
    group: "Build data",
    mode: "Dataset build",
    availability: "Scoped pilot",
    icon: Database,
    title: "Build training data your team can defend",
    summary:
      "Turn proprietary scientific records into model-ready examples with traceable sources, documented labels, and explicit exclusions.",
    bestFor:
      "Applied AI teams with a defined task, source data, and scientists available to resolve ambiguous records.",
    deliverables: [
      "Data specification covering provenance, rights, exclusions, redaction, and target schema",
      "Versioned train and validation releases with transformations, deduplication, and quality checks",
      "Dataset documentation covering label policy, coverage, known gaps, and reproducible release steps",
    ],
    boundary:
      "Dataset delivery does not include model training or promise model performance unless that work is scoped separately.",
    cta: "Scope training data",
  },
  {
    id: "agent-runtime-infrastructure",
    category: "software",
    group: "Operate safely",
    mode: "Engineering pilot",
    availability: "Scoped pilot",
    icon: Workflow,
    title: "Connect one agentic workflow to lab hardware",
    summary:
      "Build the runtime and typed device tools that let agents interact with agreed lab instruments, with approvals, retries, versioning, and recovery.",
    bestFor:
      "Lab automation and scientific software teams piloting agents through supported workcell APIs or instrument software.",
    deliverables: [
      "Runtime architecture, ownership map, durable state model, and failure model",
      "Typed tool layer with identity, permissions, approvals, retries, and stop conditions",
      "Deployed reference workflow with tests, acceptance criteria, runbook, and recovery procedure",
    ],
    boundary:
      "Only the named devices, interfaces, and actions are in scope. Agents do not bypass vendor safety controls, instrument interlocks, E-stops, or required human approval.",
    cta: "Design the agent-to-hardware pilot",
    featured: true,
  },
  {
    id: "scientific-ai-guardrails",
    category: "software",
    group: "Operate safely",
    mode: "Engineering sprint",
    availability: "Scoped pilot",
    icon: ShieldCheck,
    title: "Control what the agent can do",
    summary:
      "Put deterministic checks around the inputs, outputs, data, and tool calls that may continue, need approval, or must stop.",
    bestFor:
      "Teams allowing models to interact with consequential scientific data, software, or automation systems.",
    deliverables: [
      "Action-risk classification and permission policy for the workflow",
      "Enforced schemas, allowlists, limits, approval gates, and fail-closed behavior",
      "Failure and misuse test pack with evidence, exceptions, and an operating procedure",
    ],
    boundary:
      "These controls address named risks. They do not replace instrument interlocks, E-stops, assay review, or formal validation.",
    cta: "Design the guardrails",
  },
  {
    id: "agent-tracing-observability",
    category: "software",
    group: "Operate safely",
    mode: "Engineering sprint",
    availability: "Scoped pilot",
    icon: Route,
    title: "See every step the agent took",
    summary:
      "Trace model calls, tool calls, handoffs, approvals, failures, and generated artifacts across one scientific workflow.",
    bestFor:
      "Teams debugging multi-step agents, comparing releases, or investigating a consequential run.",
    deliverables: [
      "Trace and correlation model linking runs, tools, artifacts, approvals, and versions",
      "Instrumentation with redaction, retention, search, and export controls",
      "Run comparison and incident-review workflow with an operator investigation guide",
    ],
    boundary:
      "A trace records what the system was instrumented to capture. Logging alone does not create a compliant audit trail.",
    cta: "Trace one workflow",
  },
  {
    id: "supervised-agent-orchestration",
    category: "data",
    group: "Operate safely",
    mode: "Design partnership",
    availability: "Co-design only",
    icon: ClipboardCheck,
    title: "Pilot a scientist-supervised agent workflow",
    summary:
      "Let an agent prepare a method or run while a named scientist reviews the consequential decisions and starts execution.",
    bestFor:
      "Labs with one well-instrumented workflow, clear owners, and an appetite for controlled experimentation.",
    deliverables: [
      "Named owners, permissions, and escalation paths",
      "Preflight, review, execution, safe-stop, and recovery steps",
      "Pilot record with failures, reviewer decisions, and known limits",
    ],
    boundary:
      "Existing instrument and facility safety controls stay in charge. The agent receives only the actions approved for the pilot.",
    cta: "Scope a supervised pilot",
  },
  {
    id: "lab-mcp-development",
    category: "software",
    group: "Integrate",
    mode: "Integration project",
    availability: "Scoped pilot",
    icon: ServerCog,
    title: "Build an MCP server for your lab stack",
    summary:
      "Expose selected tools and context from a scientific system through a documented Model Context Protocol interface.",
    bestFor:
      "Platform teams and instrument or software vendors with existing APIs they want AI clients to use consistently.",
    deliverables: [
      "Tool and resource design with explicit authentication, authorization, data, and action boundaries",
      "Tested MCP server or client adapter with typed schemas, validation, and error contracts",
      "Inspector-tested examples, observability, deployment notes, and maintenance handoff",
    ],
    boundary:
      "MCP standardizes the interface; it does not repair a weak source API or make physical execution safe. We start read-only where possible.",
    cta: "Scope an MCP integration",
    featured: true,
  },
  {
    id: "workflow-code-recovery",
    category: "software",
    group: "Integrate",
    mode: "Code recovery sprint",
    availability: "Available",
    icon: GitPullRequest,
    title: "Fix or recover vibe-coded lab software",
    summary:
      "If a notebook, method, low-code flow, or agent worked once and is now unreadable or impossible to recover, we can come in and fix it.",
    bestFor:
      "Labs relying on code nobody wants to change, or on a workflow that becomes unsafe to rerun when instrument or plate state is uncertain.",
    deliverables: [
      "Workflow map and risk-ranked review of dependencies, secrets, data handling, and failure paths",
      "Characterization tests and critical-path fixes for configuration, errors, retries, idempotency, and logging",
      "Deployment checks, documentation, ownership handoff, and a scoped backlog for what remains",
    ],
    boundary:
      "A scientist still reviews undocumented biological assumptions. For physical operations, recovery uses known checkpoints and operator confirmation rather than blind retries.",
    cta: "Bring us the repo",
    featured: true,
  },
  {
    id: "method-ide",
    category: "software",
    group: "Integrate",
    mode: "Design partnership",
    availability: "Co-design only",
    icon: PanelTop,
    title: "Review scientific workflows in one place",
    summary:
      "Help shape a workspace for drafting, testing, reviewing, versioning, and handing off automation methods.",
    bestFor:
      "Scientists and automation engineers willing to co-design around one real method-development workflow.",
    deliverables: [
      "Workflow and requirements discovery",
      "Co-design around one real method",
      "Prototype plan, acceptance criteria, and a record of the decisions made together",
    ],
    boundary:
      "This is an early product collaboration built around one workflow, not a finished general-purpose IDE.",
    cta: "Explore an IDE partnership",
  },
  {
    id: "automation-sdk",
    category: "software",
    group: "Integrate",
    mode: "Design partnership",
    availability: "Co-design only",
    icon: Braces,
    title: "Build a stable interface to lab systems",
    summary:
      "Help shape SDKs and APIs that give developers typed access to methods, tests, and approved lab-system capabilities.",
    bestFor:
      "Automation engineers, platform teams, and instrument companies evaluating an integration against a real use case.",
    deliverables: [
      "Use-case and architecture discovery",
      "Interface, permission, versioning, and evidence model",
      "Prototype examples, tests, documentation, and a record of the decisions made together",
    ],
    boundary:
      "The SDK is for native software integration. Model-facing MCP interfaces are scoped as a separate offer.",
    cta: "Explore an SDK partnership",
  },
];

export default scientificAiSolutions;
